/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  APPLICATIONS_API_URL,
  ONE_OFF_CREDIT,
  PROPERTIES_API_URL,
  RENTER_URLS,
} from '@src/v3/constants/urls';
import { GeneralApplicationUnitPayload } from '@src/v3/interfaces/api/generalApplication';
import { AllApplication } from '@src/v3/interfaces/app/renter/my-application';
import { RentalHistory } from '@src/interfaces/api/userProfile';
import { RenterTUAddressProps } from '@src/interfaces/components/application';
import { OneOffUserInitialValues } from '@src/interfaces/oneOffCredit';
import {
  httpDelete,
  httpGet,
  httpPatch,
  httpPost,
  httpPut,
} from '@src/utils/http';
import { HelperParams } from '@src/interfaces/utils/http';
import { getUserId } from '@src/utils/localStorage';

/**
 * This method is used to perform API request for retrieving renter documents.
 *
 * @returns Promise
 */
const getRenterDocuments = async (
  category?: string,
  categoryOtherName?: string
) => {
  let url = RENTER_URLS.GET_DOCUMENTS;
  if (category) {
    url += `?category=${category}`;
  }
  if (categoryOtherName) {
    url += `&category_other_name=${categoryOtherName}`;
  }
  const response = await httpGet(url);

  return response;
};

/**
 * This method is used to perform API request for retrieving application for particular property.
 *
 * @returns Promise
 */
const getPropertyHasApplicationForRenter = async (
  propertyId: string,
  status?: string
) => {
  let url =
    APPLICATIONS_API_URL.IDENTIFY_PROPERTY_ASSOCIATE_WITH_RENTER.replace(
      '#RENTER_ID#',
      String(getUserId())
    );

  // Apply filter
  url += `?property_id=${propertyId}`;

  if (status) url += `&status=${status}`;

  const response = await httpGet(url);

  return response;
};

/**
 * Check application status for a specific renter on a specific property.
 * Used by agents to determine button state (View Request, View Application, etc.)
 *
 * @param renterId - The renter's ID
 * @param propertyId - The property's ID
 * @returns Promise with application data or null if no application exists
 */
const checkApplicationForRenterProperty = async (
  renterId: string,
  propertyId: string
) => {
  const url =
    APPLICATIONS_API_URL.CHECK_APPLICATION_FOR_RENTER_PROPERTY.replace(
      '#RENTER_ID#',
      renterId
    ).replace('#PROPERTY_ID#', propertyId);

  const response = await httpGet(url);
  return response;
};

/**
 * This method is used to perform API request for retrieving application for all property.
 *
 * @returns Promise
 */
const getAllApplicationForRenter = async ({
  status = '',
  limit = 20,
  offset = 0,
}: {
  status?: string;
  limit?: number;
  offset?: number;
}) => {
  let url =
    APPLICATIONS_API_URL.IDENTIFY_PROPERTY_ASSOCIATE_WITH_RENTER.replace(
      '#RENTER_ID#',
      String(getUserId())
    );

  // Build query parameters
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  params.append('limit', String(limit));
  params.append('offset', String(offset));

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const response = await httpGet(url);

  return response;
};

/**
 * This method is used to perform API request for retrieving non-invited applications for renter.
 * Uses the new API endpoint with status-based filtering.
 *
 * @param status - Filter by grouped status: 'IN_PROGRESS', 'COMPLETED', or 'UNAVAILABLE'
 * @param limit - Number of results to return
 * @param offset - Offset for pagination
 * @returns Promise
 */
const getNonInvitedApplicationsForRenter = async ({
  status = '',
  limit = 20,
  offset = 0,
}: {
  status?: string;
  limit?: number;
  offset?: number;
}) => {
  let url = APPLICATIONS_API_URL.GET_NON_INVITED_APPLICATIONS;

  // Build query parameters
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  params.append('include_general', 'true');
  params.append('limit', String(limit));
  params.append('offset', String(offset));

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const response = await httpGet(url);

  return response;
};

/**
 * This method is used to perform API request for retrieving invited applications for renter.
 * These are applications where the renter has been invited as a co-applicant or guarantor.
 *
 * @param limit - Number of results to return
 * @param offset - Offset for pagination
 * @returns Promise
 */
const getInvitedApplicationsForRenter = async ({
  limit = 20,
  offset = 0,
}: {
  limit?: number;
  offset?: number;
}) => {
  let url = APPLICATIONS_API_URL.GET_INVITED_APPLICATIONS;

  // Build query parameters
  const params = new URLSearchParams();
  params.append('limit', String(limit));
  params.append('offset', String(offset));

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const response = await httpGet(url);

  return response;
};

/**
 * This method is used to perform API request for retrieving application for all property.
 *
 * @param propertyId - The property ID
 * @param sort - Sort order for statuses
 * @param status - Filter by status (optional)
 * @param limit - Pagination limit (optional)
 * @param offset - Pagination offset (optional)
 * @param agentId - Agent ID to fetch applications for (optional, defaults to current user)
 * @returns Promise
 */
const getAllApplicationForAgent = async (
  propertyId: string,
  status?: string,
  limit?: number,
  offset?: number,
  agentId?: string
) => {
  let url = APPLICATIONS_API_URL.IDENTIFY_PROPERTY_ASSOCIATE_WITH_AGENT.replace(
    '#AGENT_ID#',
    String(agentId || getUserId())
  );
  url = url.replace('#PROPERTY_ID#', String(propertyId));

  const params = new URLSearchParams();
  if (status) {
    params.append('status', status);
  }
  if (limit !== undefined) {
    params.append('limit', String(limit));
  }
  if (offset !== undefined) {
    params.append('offset', String(offset));
  }

  url += `?${params.toString()}`;
  const response = await httpGet(url);

  return response;
};

/**
 * This method is used to perform API request for retrieving application count by status for a property.
 *
 * @param propertyId - The property ID
 * @param agentId - Optional agent ID for viewing on behalf of another agent
 * @returns Promise with count data by status
 */
const getAgentApplicationsCount = async (
  propertyId: string,
  agentId?: string
) => {
  let url = APPLICATIONS_API_URL.GET_AGENT_APPLICATIONS_COUNT.replace(
    '#PROPERTY_ID#',
    String(propertyId)
  );

  // Add agent_id query param if viewing on behalf of another agent
  if (agentId) {
    url += `?agent_id=${agentId}`;
  }

  const response = await httpGet(url);

  return response;
};

/**
 * This method is used to perform API request for retrieving renter application counts per tab.
 *
 * General Applications are opt-in on this endpoint, exactly as they are on the
 * renter lists (`getNonInvitedApplicationsForRenter`). The counts have to opt in
 * with them: they decide which tab the renter lands on, so a count taken from a
 * smaller set than the rows would open an empty tab beside a populated one.
 *
 * @returns Promise — { IN_PROGRESS, SUBMITTED, UNAVAILABLE, INVITED }
 */
const getRenterApplicationsCount = async () => {
  const response = await httpGet(
    `${APPLICATIONS_API_URL.GET_RENTER_APPLICATIONS_COUNT}?include_general=true`
  );
  return response;
};

/**
 * This method is used to perform API request to get verify document for property.
 *
 * @returns Promise
 */
const getApplicationVerifiedDocuments = async (applicationId: string) => {
  const url = APPLICATIONS_API_URL.VERIFY_DOCUMENT_RENTER.replace(
    '#RENTER_ID#',
    String(getUserId())
  ).replace('#APPLICATION_ID#', String(applicationId));

  const response = await httpGet(url);

  return response;
};

/**
 * This method is used to perform API request for creating new application for particular property.
 *
 * @returns Promise
 */
const createApplication = async (propertyId: string, email: string) => {
  const url = APPLICATIONS_API_URL.CREATE_APPLICATION;

  const params = {
    general_information: {
      property_id: propertyId,
    },
    applicant_information: {
      applicants: [
        {
          id: getUserId(),
          // phone_number: phone,
          email: email,
        },
      ],
    },
  };

  const response = await httpPost(url, params);
  return response;
};

/**
 * This method is used to perform API request to attach selected document into application.
 *
 * @returns Promise
 */
const attachDocumentsToApplication = async (
  applicationId: string,
  documents: { id: string }[],
  category: string,
  categoryLabel: string
) => {
  let params: {
    documents: { id: string }[];
    category: string;
    category_other_name?: string;
  } = {
    documents,
    category,
  };

  if (category === 'other')
    params = { ...params, category_other_name: categoryLabel };

  const url = APPLICATIONS_API_URL.ATTACH_DOCUMENTS_TO_APPLICATION.replace(
    '#RENTER_ID#',
    String(getUserId())
  ).replace('#APPLICATION_ID#', String(applicationId));

  const response = await httpPut(url, params);

  return response;
};

/**
 * This method is used to perform API request to submit application for primary renter.
 * @param {string} applicationId
 * @returns promise
 */
const submitApplicationForPrimaryRenter = async (applicationId: string) => {
  const url = APPLICATIONS_API_URL.SUBMIT_APPLICATION_RENTER;
  const response = await httpPost(
    `${url
      .replace('#RENTER_ID#', String(getUserId()))
      .replace('#APPLICATION_ID#', String(applicationId))}`,
    {}
  );

  return response;
};

/**
 * This method is used perform API request to update annual income.
 */
const updateRenterIncome = async (
  income: number | string,
  name: string,
  placeId: string,
  email: string,
  isUnemployed: boolean,
  unemployedReason: string,
  jobTitle: string,
  selectedMonth: number,
  selectedYear: number,
  managerName: string,
  managerPhoneNumber: string
) => {
  let params = {
    renter_information: {
      financial_information: {
        salary_information: {
          reported_salary: Number(income),
        },
        employment_information: {
          is_unemployed: isUnemployed || false,
          unemployment_reason: isUnemployed ? unemployedReason : '',
        },
      },
    },
  };
  if (!isUnemployed) {
    const paramsWithEmploymentInfo = {
      ...params,
      renter_information: {
        ...params.renter_information,
        financial_information: {
          ...params.renter_information.financial_information,
          employment_information: {
            ...params.renter_information.financial_information
              .employment_information,
            employer_name: name,
            employer_email: email,
            employer_address_place_id: placeId,
            job_title: jobTitle || '',
            employment_length_in_years: selectedYear || 0,
            employment_length_in_months: selectedMonth || 0,
            manager_name: managerName || '',
            manager_phone_number: managerPhoneNumber || '',
          },
        },
      },
    };
    params = { ...paramsWithEmploymentInfo };
  }

  const id = getUserId();

  const response = await httpPatch(
    `${RENTER_URLS.UPDATE_PROFILE.replace('#ID#', String(id))}`,
    params
  );

  return response;
};

/**
 * This method is used perform API request to update annual income.
 */
const updateVoucherInformation = async (
  voucherType: string,
  issuingAgency: string,
  voucherAmount: string,
  caseWorkerName: string,
  caseWorkerEmail: string,
  caseWorkerPhone: string
) => {
  const params = {
    renter_information: {
      financial_information: {
        employment_information: {
          voucher_information: {
            voucher_type: voucherType,
            issuing_agency: issuingAgency,
            voucher_amount: voucherAmount,
            case_worker_name: caseWorkerName,
            case_worker_number: caseWorkerPhone,
            case_worker_email: caseWorkerEmail,
          },
        },
      },
    },
  };

  const id = getUserId();

  const response = await httpPatch(
    `${RENTER_URLS.UPDATE_PROFILE.replace('#ID#', String(id))}`,
    params
  );

  return response;
};

/**
 * This method is used perform API request to update renter history.
 */
const updateRenterHistory = async (history: RentalHistory[]) => {
  const params = {
    renter_information: {
      rental_histories: history,
    },
  };

  const id = getUserId();

  const response = await httpPatch(
    `${RENTER_URLS.UPDATE_PROFILE.replace('#ID#', String(id))}`,
    params
  );

  return response;
};

/**
 * Retrieves an application by its ID from the server.
 *
 * Pass `{ hideError: true }` when the fetch is a refetch that only keeps the
 * screen in step with the server: its failure is not what the user just did, and
 * the generic toast would land on top of -- or ahead of -- the result of the
 * action they were actually taking.
 *
 * @param {number} applicationId - The ID of the application to retrieve.
 * @param {HelperParams} [options]
 * @return {Promise<any>} - A promise that resolves to the response from the server.
 */
const getApplicationById = async (
  applicationId: string,
  options?: HelperParams
) => {
  const response = await httpGet(
    `${APPLICATIONS_API_URL.GET_SINGLE_APPLICATION.replace(
      '#APPLICATION_ID#',
      String(applicationId)
    )}`,
    options
  );

  return response;
};

/**
 * This method is used to perform API request to get checkout view.
 * @param {String} applicationId
 * @returns Promise
 */
const getCheckoutView = async (applicationId: string) => {
  const response = await httpPost(
    `${APPLICATIONS_API_URL.APPLICATION_CHECKOUT_VIEW.replace(
      '#APPLICATION_ID#',
      String(applicationId)
    )}`,
    {}
  );

  return response;
};

/**
 * This method is used to perform API request to update payment details of application.
 * Currently it's fake [TODO: it will be update]
 * @returns Promise
 */
const patchApplicationPayment = async () => {
  const url =
    APPLICATIONS_API_URL.IDENTIFY_PROPERTY_ASSOCIATE_WITH_RENTER.replace(
      '#RENTER_ID#',
      String(getUserId())
    );

  const response = await httpGet(url);

  return response;
};

/*
 * This method is used to perform API request to get Signing lease session from docuSign.
 * @param {String} applicationId
 * @returns Promise
 */
const getSignLeaseSession = async (applicationId: string) => {
  const response = await httpPost(
    `${APPLICATIONS_API_URL.GET_DOCUMENT_SIGN_SIGNING_SESSION.replace(
      '#RENTER_ID#',
      String(getUserId())
    ).replace('#APPLICATION_ID#', String(applicationId))}`,
    {}
  );

  return response;
};

/**
 * This method is used to perform API request to notify sign lease session from docuSign.
 * @param {String} applicationId
 * @returns promise
 */
const notifySignLease = async (applicationId: string) => {
  const response = await httpPost(
    `${APPLICATIONS_API_URL.NOTIFY_SIGN_LEASE.replace(
      '#APPLICATION_ID#',
      applicationId
    )}`,
    {}
  );

  return response;
};

/**
 * This method is used to perform API request to create send lease session from docuSign.
 * @param {String} applicationId
 * @param {String} document
 * @param {String} email
 * @param {String} landlord_first_name
 * @param {String} landlord_last_name
 * @returns Promise
 */
const getSendLeaseSession = async (
  applicationId: string,
  document: string | null,
  email: string,
  landlord_first_name: string,
  landlord_last_name: string
) => {
  let params = {};
  if (email) {
    params = {
      document,
      landlord_first_name,
      landlord_last_name,
      landlord_email: email,
    };
  }

  if (!email && document) {
    params = {
      document,
    };
  }

  const response = await httpPost(
    `${APPLICATIONS_API_URL.CREATE_SEND_LEASE_SESSION.replace(
      '#APPLICATION_ID#',
      String(applicationId)
    )}`,
    params
  );

  return response;
};

/**
 * This method is used to perform API request to request about payment to renter by agent
 * @param {String} applicationId
 * @returns Promise
 */
const paymentRequestByAgent = async (applicationId: string) => {
  const response = await httpPost(
    `${APPLICATIONS_API_URL.CREATE_SEND_LEASE_SESSION.replace(
      '#APPLICATION_ID#',
      String(applicationId)
    )}`,
    {}
  );

  return response;
};

/**
 * This method is used to perform API request to do application payment by renter. It will generate stripe checkout session link.
 * @param {String} applicationId
 * @returns Promise
 */
const doApplicationPaymentByRenter = async (applicationId: string) => {
  const response = await httpPost(
    `${APPLICATIONS_API_URL.APPLICATION_CHECKOUT_VIEW.replace(
      '#APPLICATION_ID#',
      String(applicationId)
    )}`,
    {}
  );

  return response;
};

const acceptRejectApplicationByAgent = async (
  applicationId: string,
  agentId: string,
  action: string
) => {
  const url =
    action === 'accept'
      ? APPLICATIONS_API_URL.ACCEPT_APPLICATION_BY_AGENT
      : APPLICATIONS_API_URL.DENY_APPLICATION_BY_AGENT;

  const response = await httpPost(
    `${url
      .replace('#APPLICATION_ID#', String(applicationId))
      .replace('#AGENT_ID#', String(agentId))}`,
    {}
  );

  return response;
};

const cancelApplicationByRenter = async (applicationId: string) => {
  const response = await httpPost(
    `${APPLICATIONS_API_URL.CANCEL_APPLICATION_BY_RENTER.replace(
      '#APPLICATION_ID#',
      String(applicationId)
    ).replace('#RENTER_ID#', String(getUserId()))}`,
    {}
  );

  return response;
};

/**
 * Retrieves the Finix payment session for a given application.
 *
 * @param {string} applicationId
 * @param {string} token
 * @return {Promise<any>} - The response from the HTTP POST request.
 */
const getFinixPaymentSession = async (
  applicationId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  token: any,
  type: string,
  address?: any
) => {
  const params: { token: string; type?: string; address?: any } = {
    token,
  };

  if (type) {
    params.type = type;
  }

  if (address) {
    params.address = {
      line1: address.address1,
      city: address.locality,
      country: address.countryCode,
      postal_code: address.postalCode,
      region: address.administrativeArea,
    };
  }

  const response = await httpPost(
    `${APPLICATIONS_API_URL.FINIX_PAYMENT_SESSION.replace(
      '#APPLICATION_ID#',
      String(applicationId)
    )}`,
    params
  );

  return response;
};

/**
 * Retrieves the Finix payment session for a given application.
 *
 * @param {string} applicationId
 * @param {string} token
 * @return {Promise<any>} - The response from the HTTP POST request.
 */
const getFinixPaymentSessionForOneOffCredit = async (
  applicationId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  token: any,
  type: string,
  userData: OneOffUserInitialValues,
  address?: any
) => {
  const params: {
    token: string;
    type?: string;
    address?: any;
    application_id: string;
    first_name: string;
    middle_name: string;
    last_name: string;
    email: string;
    phone: string;
  } = {
    token,
    application_id: applicationId,
    first_name: userData?.first_name ?? '',
    middle_name: userData?.middle_name ?? '',
    last_name: userData?.last_name ?? '',
    email: userData?.email ?? '',
    phone: userData?.phone ?? '',
  };
  if (type) {
    params.type = type;
  }

  if (address) {
    params.address = {
      line1: address.address1,
      city: address.locality,
      country: address.countryCode,
      postal_code: address.postalCode,
      region: address.administrativeArea,
    };
  }

  const response = await httpPost(
    `${ONE_OFF_CREDIT.FINIX_PAYMENT_SESSION.replace(
      '#APPLICATION_ID#',
      String(applicationId)
    )}`,
    params
  );

  return response;
};

/**
 * Retrieves the Finix payment session for a given application.
 *
 * @param {string} applicationId
 * @param {string} token
 * @return {Promise<any>} - The response from the HTTP POST request.
 */
const getFinixPaymentSessionWithAmount = async (
  amount: number,
  address: {
    city: string;
    country: string;
    region: string;
    line1: string;
    postal_code: string;
  },
  token: string
) => {
  const params = {
    token,
    amount,
    address,
  };
  const response = await httpPost(
    `${APPLICATIONS_API_URL.FAKE_FINIX_PAYMENT_SESSION}`,
    params
  );

  return response;
};

/**
 * This method is used to perform API request to generate transunion session in which we get screening_id.
 * @param {string} applicationId
 * @param {object} options
 * @returns Promise
 */
const generateTUSessionFromApi = async (
  applicationId: string,
  options: {
    address?: RenterTUAddressProps;
    social_security_number?: string;
    date_of_birth?: string;
  }
) => {
  const url = APPLICATIONS_API_URL.TU_GENERATE_SCREENING_SESSION;

  const response = await httpPost(
    `${url.replace('#APPLICATION_ID#', String(applicationId))}`,
    options,
    { hideError: true }
  );

  return response;
};

/**
 * This method is used to perform API request to generate transunion session in which we get screening_id.
 * @param {string} applicationId
 * @param {object} options
 * @returns Promise
 */
const generateTUSessionFromOneOffCredit = async (
  applicationId: string,
  options: {
    first_name: string;
    middle_name: string;
    last_name: string;
    property_address: string;
    address: RenterTUAddressProps;
    social_security_number: string;
    date_of_birth: string;
    email: string;
  }
) => {
  const url = ONE_OFF_CREDIT.TU_GENERATE_SCREENING_SESSION;
  const params = { application_id: applicationId, ...options };
  const response = await httpPost(url, params, { hideError: true });

  return response;
};

/**
 * This method is used to check the credit check status for one-off credit user.
 *
 * @param {string} applicationId
 * @returns Promise
 */
const checkOneOffCreditStatus = async (applicationId: string) => {
  const url = `${ONE_OFF_CREDIT.CREDIT_CHECK_STATUS.replace(
    '#APPLICATION_ID#',
    applicationId
  )}`;

  const response = await httpGet(url);

  return response;
};

/**
 * This method is used to perform API request to get renter attestations.
 * @param {string} screeningId
 * @returns Promise
 */
const getRenterAttestationsFromApi = async (screeningId: string) => {
  const url = APPLICATIONS_API_URL.GET_RENTER_ATTESTATIONS.replace(
    '#SCREENING_ID#',
    String(screeningId)
  );

  const response = await httpGet(url);

  return response;
};

/**
 * This method is used to perform API request to accept renter attestations.
 * @param {string} applicationId
 * @returns Promise
 */
const acceptRenterAttestationsToApi = async (applicationId: string) => {
  const params = {
    application_id: applicationId,
    accept: true,
  };
  const url = APPLICATIONS_API_URL.ACCEPT_RENTER_ATTESTATIONS;

  const response = await httpPost(url, params);

  return response;
};

/**
 * This method is used to perform API request to generate transunion session in which we get multiple questions exam to verify.
 * @param {string} applicationId
 * @param {object} options
 * @returns Promise
 */
const generateTUScreeningQuestionsFromApi = async (
  applicationId: string,
  screeningId: string
) => {
  const url = APPLICATIONS_API_URL.TU_GENERATE_SCREENING_QUESTIONS;

  const response = await httpPost(
    `${url
      .replace('#APPLICATION_ID#', String(applicationId))
      .replace('#SCREENING_ID#', String(screeningId))}`,
    { hideError: true }
  );

  return response;
};

/**
 * This method is used to perform API request to get landlord attestations.
 * @param {string} propertyId
 * @returns Promise
 */
const getLandlordAttestationsFromApi = async (propertyId: string) => {
  const url = PROPERTIES_API_URL.GET_LANDLORD_ATTESTATIONS.replace(
    '#PROPERTY_ID#',
    String(propertyId)
  );

  const response = await httpGet(url);

  return response;
};

/**
 * This method is used to perform API request to accept renter attestations.
 * @param {string} applicationId
 * @returns Promise
 */
const acceptLandlordAttestationsToApi = async (propertyId: string) => {
  const params = {
    property_id: propertyId,
    accept: true,
  };
  const url = PROPERTIES_API_URL.ACCEPT_LANDLORD_ATTESTATIONS;

  const response = await httpPost(url, params);

  return response;
};

/**
 * This method is used to perform API request to generate transunion session in which we get multiple questions exam to verify.
 * @param {string} examId
 * @param {object} params
 * @returns Promise
 */
const answerTUSessionToApi = async (
  examId: string,
  params: {
    question_keys: string[];
    choice_keys: string[];
    screening_renter_id: string;
    application_id: string;
  }
) => {
  const url = APPLICATIONS_API_URL.TU_SCREENING_ANSWER_SESSION;

  const response = await httpPost(
    `${url.replace('#EXAM_ID#', String(examId))}`,
    params
  );

  return response;
};

/**
 * Fetches the details of the invite token.
 * @param {string} inviteToken - The invite token.
 * @returns {Promise<Object>} The response from the API.
 */
const fetchInviteTokenDetails = async (inviteToken: string) => {
  const url = APPLICATIONS_API_URL.FETCH_INVITE_TOKEN_DETAILS?.replace(
    '#INVITE_TOKEN#',
    inviteToken
  );
  const response = await httpGet(url);
  return response;
};

/**
 * Adds co-applicants or guarantors to an application via API.
 *
 * @param {Object} params - The parameters containing the co-applicants or guarantors to add.
 * @param {Array} params.coapplicants - The list of co-applicants or guarantors to add.
 * @param {string} params.coapplicants[].phone_number - The phone number of the co-applicant or guarantor.
 * @param {boolean} [params.coapplicants[].is_guarantor] - Whether the invitee is a guarantor.
 * @param {string} applicationId - The ID of the application to which the co-applicants or guarantors are being added.
 * @returns {Promise<Object>} The response from the API.
 */

const addCoApplicantsOrGuarantorToApi = async (
  params: {
    coapplicants: {
      email: string;
      is_guarantor?: boolean;
    }[];
  },
  applicationId: string
) => {
  const url = APPLICATIONS_API_URL.ADD_CO_APPLICANT;

  const response = await httpPost(
    `${url.replace('#APPLICATION_ID#', String(applicationId))}`,
    params
  );

  return response;
};

/**
 * Removes a co-applicant or guarantor from the API.
 *
 * @param {Object} params - The parameters for removing a co-applicant or guarantor.
 * @param {string} params.phoneNumber - The phone number of the co-applicant or guarantor.
 * @param {string} applicationId - The ID of the application.
 * @returns {Promise<Object>} The response from the API.
 */
const removeCoApplicantsOrGuarantorToApi = async (
  email: string,
  applicationId: string
) => {
  const url = APPLICATIONS_API_URL.DELETE_CO_APPLICANT;
  const params = {
    email,
  };
  const response = await httpDelete(
    `${url.replace('#APPLICATION_ID#', String(applicationId))}`,
    params
  );

  return response;
};

/**
 * Accept/Reject the co-applicant invitation for a specific application.
 *
 * @param {string} applicationId - The ID of the application.
 * @param {string} action - The action to be performed.
 * @return {Promise} The response from the API request.
 */
const acceptRejectCoApplicantInvited = async (
  applicationId: string,
  action: string
) => {
  const url =
    action === 'accept'
      ? APPLICATIONS_API_URL.ACCEPT_CO_APPLICANT_INVITE
      : APPLICATIONS_API_URL.REJECT_CO_APPLICANT_INVITE;

  const response = await httpPost(
    `${url.replace('#APPLICATION_ID#', String(applicationId))}`,
    {}
  );

  return response;
};

/**
 * This method is used to perform API request to get documents PDF for Agent.
 *
 * @returns Promise
 */
const getApplicationPDFForAgent = async (
  applicationId: string,
  onBehalfOfAgentId?: string
) => {
  const url = APPLICATIONS_API_URL.GET_AGENT_DOCUMENTS_PDF.replace(
    '#APPLICATION_ID#',
    String(applicationId)
  );

  const payload: Record<string, string> = {};
  if (onBehalfOfAgentId) {
    payload.on_behalf_of_agent_id = onBehalfOfAgentId;
  }

  const response = await httpPost(url, payload);

  return response;
};

/**
 * This method is used to perform API request to get documents PDF for Agent per applicant.
 *
 * @returns Promise
 */
const getApplicationPDFForAgentPerApplicant = async (
  applicationId: string,
  applicantId: string,
  onBehalfOfAgentId?: string
) => {
  const url =
    APPLICATIONS_API_URL.GET_AGENT_DOCUMENTS_PDF_PER_APPLICANT.replace(
      '#APPLICATION_ID#',
      String(applicationId)
    ).replace('#APPLICANT_ID#', String(applicantId));

  const payload: Record<string, string> = {};
  if (onBehalfOfAgentId) {
    payload.on_behalf_of_agent_id = onBehalfOfAgentId;
  }

  const response = await httpPost(url, payload);

  return response;
};

/**
 * Reorders the documents.
 *
 * @param {Document[]} documents - The array of documents to be reordered.
 * @return {Promise<any>} - A promise that resolves to the response from the server.
 */
const reorderDocuments = async (params: {
  documents: {
    id: string;
    main_sequence_number: number;
    sub_sequence_number: number | null;
    is_hidden: boolean;
  }[];
}) => {
  const url = APPLICATIONS_API_URL.REORDER_DOCUMENTS;

  const response = await httpPost(url, params);

  return response;
};

/**
 * Generates an Apple session using the provided parameters.
 *
 * @param {Object} params - The parameters for generating the Apple session.
 * @param {string} params.provider - The provider for the Apple session.
 * @param {string} params.validation_url - The validation URL for the Apple session.
 * @param {string} params.merchant_identifier - The merchant identifier for the Apple session.
 * @param {string} params.domain_name - The domain name for the Apple session.
 * @param {string} params.display_name - The display name for the Apple session.
 * @return {Promise<any>} The response from the Apple session generation.
 */
const generateAppleSession = async (params: {
  validation_url: string;
  merchant_identity: string;
  domain: string;
  display_name: string;
}) => {
  const url = APPLICATIONS_API_URL.FINIX_GENERATE_APPLE_SESSION;

  const response = await httpPost(url, params);

  return response;
};

/**
 * Asynchronously skips the lease for the given application ID.
 *
 * @param {string} applicationId - The ID of the application
 * @return {Promise<any>} The response from skipping the lease
 */
const skipLease = async (applicationId: string) => {
  const url = APPLICATIONS_API_URL?.SKIP_LEASE?.replace(
    '#APPLICATION_ID#',
    String(applicationId)
  );
  const response = await httpPost(url, {});

  return response;
};

/**
 * Generates a fair housing form signing with the provided agent ID and signature.
 *
 * @param {string} agentId - The ID of the agent signing the form.
 * @param {string} signature - The signature for the fair housing form.
 * @return {Promise<any>} The response from the API call after signing the form.
 */
const signFairHousingFormToAPI = async (agentId: string, signature: string) => {
  const params = {
    agent_id: agentId,
    signature,
  };
  const url = RENTER_URLS?.SIGN_FAIR_HOUSING;
  const response = await httpPost(url, params);

  return response;
};

/**
 * Get whether the renter has already signed the fair housing form.
 *
 * @return {Promise<any>} The response from the API indicating if the renter has signed the form.
 */
const getIsRenterAlreadySignFairHousingForm = async (agentId: string) => {
  const params = {
    agent_id: agentId,
  };

  const url = RENTER_URLS.GET_IS_RENTER_ALREADY_SIGN_FAIR_HOUSING_FORM;
  const response = await httpPost(url, params);

  return response;
};

/**
 * Reopens an application by making an HTTP POST request to the APPLICATIONS_API_URL.REOPEN_APPLICATION endpoint.
 *
 * @param {string} applicationId - The ID of the application to be reopened.
 * @return {Promise<any>} A Promise that resolves with the response from the API.
 */
const reOpenApplication = async (
  applicationId: string,
  onBehalfOfAgentId?: string
) => {
  const params = onBehalfOfAgentId
    ? { on_behalf_of_agent_id: onBehalfOfAgentId }
    : {};

  const url = APPLICATIONS_API_URL.REOPEN_APPLICATION.replace(
    '#APPLICATION_ID#',
    String(applicationId)
  );
  const response = await httpPost(url, params);

  return response;
};

/**
 * Sends a resend email request for a specific applicant identified by applicationId.
 *
 * @param {string} applicationId - The ID of the application for the applicant.
 * @param {string} email - The email of the applicant.
 * @return {Promise<any>} A Promise that resolves with the response from the API.
 */
const resendEmailForApplicant = async (
  applicationId: string,
  email: string
) => {
  const params = {
    email,
  };

  const url = APPLICATIONS_API_URL.RESEND_EMAIL_FOR_APPLICANT.replace(
    '#APPLICATION_ID#',
    String(applicationId)
  );

  const response = await httpPost(url, params);

  return response;
};

/**
 * Updates an application with the specified applicationId.
 *
 * @param {string} applicationId - The ID of the application to update.
 * @param {boolean} isShareInProgressApplication - Flag to share in-progress application with agent.
 * @return {Promise<any>} A Promise that resolves with the response from the API.
 */
const updateApplication = async (
  applicationId: string,
  isShareInProgressApplication: boolean
) => {
  const params = {
    general_information: {
      share_inprogress_application_with_agent: isShareInProgressApplication,
    },
  };

  const response = await httpPatch(
    `${APPLICATIONS_API_URL.UPDATE_APPLICATION.replace(
      '#APPLICATION_ID#',
      String(applicationId)
    )}`,
    params
  );

  return response;
};

/**
 * Corrects the unit on a General Application — the free text the renter typed to
 * start it. Same endpoint as `updateApplication`; the API accepts these two keys
 * only for a general application, only from the primary applicant, and only while
 * the application is IN_PROGRESS. It trims both and stores a blank unit number as
 * null. The response is the full application.
 *
 * Sent with `hideError` because the caller shows the failure itself: validation
 * comes back nested, `{ general_information: { unit_address: [...] } }`, and a
 * state or permission refusal comes back as `{ error: '...' }`.
 *
 * @param {string} applicationId - The ID of the application to update.
 * @param {GeneralApplicationUnitPayload} unit - The corrected address and number.
 * @return {Promise<AllApplication>} A Promise that resolves with the updated application.
 */
const updateGeneralApplicationUnit = async (
  applicationId: string,
  unit: GeneralApplicationUnitPayload
): Promise<AllApplication> =>
  await httpPatch(
    APPLICATIONS_API_URL.UPDATE_APPLICATION.replace(
      '#APPLICATION_ID#',
      String(applicationId)
    ),
    { general_information: unit },
    { hideError: true }
  );

/**
 * Updates an application with the specified applicationId.
 *
 * @param {string} applicationId - The ID of the application to update.
 * @param {string} applicantId - The ID of the applicant to update.
 * @param {boolean} isVoucherAvailabel - Flag to update voucher selection in application.
 * @return {Promise<any>} A Promise that resolves with the response from the API.
 */
const updateApplyVoucherToApplication = async (
  applicationId: string,
  applicantId: string,
  isVoucherAvailabel: boolean
) => {
  const params = {
    applicant_information: {
      applicants: [
        {
          id: applicantId,
          is_voucher_available: isVoucherAvailabel,
        },
      ],
    },
  };

  const response = await httpPatch(
    `${APPLICATIONS_API_URL.UPDATE_APPLICATION.replace(
      '#APPLICATION_ID#',
      String(applicationId)
    )}`,
    params
  );

  return response;
};

/**
 * Executes a credit bypass for a specific applicant within an application.
 *
 * @param {string} applicationId - The ID of the application.
 * @param {string} applicantId - The ID of the applicant.
 * @return {Promise<any>} A Promise that resolves with the response from the API.
 */
const creditBypass = async (applicationId: string, applicantId: string) => {
  const url = APPLICATIONS_API_URL.CREDIT_BYPASS.replace(
    '#APPLICATION_ID#',
    String(applicationId)
  ).replace('#APPLICANT_ID#', String(applicantId));
  const response = await httpPost(url, {});

  return response;
};

const getRenterTransunionInfo = async () => {
  const response = await httpGet(
    `${APPLICATIONS_API_URL.GET_RENTER_TRANSUNION_INFORMATION}`
  );
  return response;
};

const updateRenterTransunionInfo = async (params: {
  first_name: string;
  middle_name: string;
  last_name: string;
  address: RenterTUAddressProps;
  date_of_birth: string;
  national_id: string;
}) => {
  const response = await httpPost(
    `${APPLICATIONS_API_URL.UPDATE_RENTER_TRANSUNION_INFORMATION}`,
    params
  );

  return response;
};
/**
 * Executes a credit bypass revert for a specific applicant within an application.
 *
 * @param {string} applicationId - The ID of the application.
 * @param {string} applicantId - The ID of the applicant.
 * @return {Promise<any>} A Promise that resolves with the response from the API.
 */
const revertcreditByPass = async (
  applicationId: string,
  applicantId: string
) => {
  const url = APPLICATIONS_API_URL.REVERT_CREDIT_BYPASS.replace(
    '#APPLICATION_ID#',
    String(applicationId)
  ).replace('#APPLICANT_ID#', String(applicantId));
  const response = await httpPost(url, {});

  return response;
};

/**
 * Accepts an application request by making an HTTP POST request to the API.
 *
 * @param {string} applicationId - The ID of the application request to accept.
 * @return {Promise<any>} A Promise that resolves with the response from the API.
 */
const acceptApplicationRequest = async (applicationId: string) => {
  const url = APPLICATIONS_API_URL.ACCEPT_REQUEST_APPLICATION.replace(
    '#APPLICATION_ID#',
    String(applicationId)
  );

  const response = await httpPost(url, {});

  return response;
};

/**
 * Fetches the TransUnion state list for the credit check form.
 * @returns Promise<{ states: { name: string; short_name: string }[] }>
 */
const getTransunionStateList = async () => {
  const response = await httpGet(
    APPLICATIONS_API_URL.GET_TRANSUNION_STATE_LIST
  );
  return response;
};

/**
 * Checks if there is an invite for the current renter.
 * @return {Promise<{ is_invited: boolean }>} A Promise that resolves with an object containing a boolean indicating whether there is an invite.
 */
const checkRenterInvite = async () => {
  const response = await httpGet(APPLICATIONS_API_URL.CHECK_RENTER_INVITE);
  return response;
};

/**
 * Allows the agent to request access to the background check for a specific applicant.
 *
 * @param {string} applicationId - The ID of the application.
 * @param {string} applicantId - The ID of the applicant for whom background access is being requested.
 * @return {Promise<any>} A Promise that resolves with the response from the API.
 */

const updateAgentCanAccesBackground = async (
  applicationId: string,
  applicantId: string
) => {
  const response = await httpPost(
    APPLICATIONS_API_URL.AGENT_ACCESS_BACKGROUND.replace(
      '#APPLICATION_ID#',
      applicationId
    ).replace('#APPLICANT_ID#', applicantId),
    { agent_can_access_background: true }
  );
  return response;
};

/**
 * Allows the agent to request access to the background check for a specific applicant.
 *
 * @param {string} applicantId - The ID of the applicant for whom background access is being requested.
 * @return {Promise<any>} A Promise that resolves with the response from the API.
 */

const enableBackgroudnCheckForOneOff = async (applicationId: string) => {
  const response = await httpPost(
    ONE_OFF_CREDIT.ENABLE_BACKGROUND_CHECK.replace(
      '#APPLICATION_ID#',
      applicationId
    ),
    {}
  );
  return response;
};

/**
 * Upload documents for an application
 * @param applicationId The ID of the application
 * @param formData The form data containing the documents
 * @returns Promise with the uploaded documents
 */
const uploadApplicationDocuments = async (
  applicationId: string,
  formData: FormData
): Promise<any> => {
  const response = await httpPut(
    `${APPLICATIONS_API_URL.UPLOAD_APPLICATION_DOCUMENTS.replace(
      '#APPLICATION_ID#',
      applicationId
    )}`,
    formData,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return response;
};

/**
 * Delete a document from an application
 * @param applicationId The ID of the application
 * @param applicantId The ID of the applicant
 * @param documentId The ID of the document to delete
 * @returns Promise with the deletion result
 */
const deleteApplicationDocument = async (
  applicationId: string,
  applicantId: string,
  documentId: string
): Promise<any> => {
  const url = APPLICATIONS_API_URL.DELETE_APPLICATION_DOCUMENT.replace(
    '#APPLICATION_ID#',
    applicationId
  )
    .replace('#APPLICANT_ID#', applicantId)
    .replace('#DOCUMENT_ID#', documentId);

  const response = await httpDelete(url);
  return response;
};

export {
  getRenterDocuments,
  getPropertyHasApplicationForRenter,
  checkApplicationForRenterProperty,
  getAllApplicationForRenter,
  getNonInvitedApplicationsForRenter,
  getInvitedApplicationsForRenter,
  getAllApplicationForAgent,
  getAgentApplicationsCount,
  getRenterApplicationsCount,
  submitApplicationForPrimaryRenter,
  createApplication,
  attachDocumentsToApplication,
  getApplicationVerifiedDocuments,
  updateRenterIncome,
  updateVoucherInformation,
  updateRenterHistory,
  getCheckoutView,
  getApplicationById,
  patchApplicationPayment,
  getSignLeaseSession,
  getSendLeaseSession,
  paymentRequestByAgent,
  doApplicationPaymentByRenter,
  acceptRejectApplicationByAgent,
  cancelApplicationByRenter,
  notifySignLease,
  getFinixPaymentSession,
  getFinixPaymentSessionForOneOffCredit,
  getFinixPaymentSessionWithAmount,
  generateTUSessionFromApi,
  generateTUSessionFromOneOffCredit,
  checkOneOffCreditStatus,
  getRenterAttestationsFromApi,
  acceptRenterAttestationsToApi,
  generateTUScreeningQuestionsFromApi,
  getLandlordAttestationsFromApi,
  acceptLandlordAttestationsToApi,
  answerTUSessionToApi,
  fetchInviteTokenDetails,
  addCoApplicantsOrGuarantorToApi,
  removeCoApplicantsOrGuarantorToApi,
  acceptRejectCoApplicantInvited,
  getApplicationPDFForAgent,
  getApplicationPDFForAgentPerApplicant,
  reorderDocuments,
  generateAppleSession,
  skipLease,
  signFairHousingFormToAPI,
  getIsRenterAlreadySignFairHousingForm,
  reOpenApplication,
  resendEmailForApplicant,
  updateApplication,
  updateGeneralApplicationUnit,
  updateApplyVoucherToApplication,
  creditBypass,
  getRenterTransunionInfo,
  updateRenterTransunionInfo,
  revertcreditByPass,
  acceptApplicationRequest,
  getTransunionStateList,
  checkRenterInvite,
  updateAgentCanAccesBackground,
  enableBackgroudnCheckForOneOff,
  uploadApplicationDocuments,
  deleteApplicationDocument,
};
